import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const ThemeContext = createContext({
  mode: 'dark',
  theme: 'dark',
  setMode: () => {},
  toggleTheme: () => {},
});

const STORAGE_KEY = 'impulso-theme';
const VALID_MODES = ['light', 'dark', 'system'];

function applyHtmlClass(resolvedTheme) {
  const html = document.documentElement;
  html.classList.remove('light', 'dark');
  html.classList.add(resolvedTheme);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', resolvedTheme === 'light' ? '#f5f5f7' : '#0A0A0A');
}

function getSystemTheme() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(mode) {
  return mode === 'system' ? getSystemTheme() : mode;
}

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return VALID_MODES.includes(saved) ? saved : 'dark';
    } catch {
      return 'dark';
    }
  });

  const [theme, setTheme] = useState(() => resolveTheme(mode));

  // Aplica classe e persiste ao mudar mode
  useEffect(() => {
    const resolved = resolveTheme(mode);
    setTheme(resolved);
    applyHtmlClass(resolved);
    try { localStorage.setItem(STORAGE_KEY, mode); } catch {}
  }, [mode]);

  // Ouve mudanças do sistema quando mode='system'
  useEffect(() => {
    if (mode !== 'system' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      const resolved = getSystemTheme();
      setTheme(resolved);
      applyHtmlClass(resolved);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [mode]);

  const setMode = useCallback((next) => {
    setModeState(VALID_MODES.includes(next) ? next : 'dark');
  }, []);

  // Ciclo: dark -> light -> system -> dark
  const toggleTheme = useCallback(() => {
    setModeState((cur) => {
      if (cur === 'dark') return 'light';
      if (cur === 'light') return 'system';
      return 'dark';
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ mode, theme, setMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
